import { action, makeObservable, observable, runInAction } from 'mobx'
import { computedFn } from 'mobx-utils'

import { UUID } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { TapeType } from '@new/domains/fastBookingsTapeChart/__types'
import { PropertyInfoStore } from '@new/domains/properties/stores/PropertyInfoStore'

import { computedRouteParam } from '@stores/computedRouteParam'
import { SidePanelStore, SidePanelType } from '@stores/SidePanelStore'

import { BaseStore } from '../__types'
import { fastTapeChartPaths } from '../config/contants'
import { scrollController } from '../ScrollController'
import { NavigationStore } from './NavigationStore'
import { TapesStore } from './TapesStore'

export class UiStateStore {
  constructor(
    private getApiStore: () => BaseStore['apiStore'],
    private getNavigationStore: () => NavigationStore,
    private getTapesStore: () => TapesStore,
    private getFiltersStore: () => BaseStore['filterStore'],
  ) {
    makeObservable(this, {
      selectedGroupRowId: observable,
      checkInitialUrlParams: action.bound,
      goToPropertyPage: action.bound,
      selectProperty: action.bound,
      showListingInfo: action.bound,
      setSelectedGroupRowId: action.bound,
    })
  }

  get apiStore() {
    return this.getApiStore()
  }

  get navigationStore() {
    return this.getNavigationStore()
  }

  get tapesStore() {
    return this.getTapesStore()
  }

  get filtersStore() {
    return this.getFiltersStore()
  }

  selectedGroupRowId?: string

  checkIsListItemSelected = computedFn((listItemKey?: UUID) => {
    return PropertyInfoStore.currentHomeId === listItemKey
  })

  async checkInitialUrlParams() {
    const bookingId = this.filtersStore?.bookingId
    const propertyId = this.filtersStore?.propertyId

    const selectedArrivalChecklistHomeId = computedRouteParam<string | undefined>(
      'selectedArrivalChecklistHomeId',
      { patterns: fastTapeChartPaths },
    ).get()

    if (bookingId) {
      runInAction(() => {
        SidePanelStore.setCurrentSidePanel(SidePanelType.hotelTapeChartReservationInfo, {
          screenHasSecondaryMenu: true,
        })
        this.tapesStore.showTapeSidePanel()
      })

      runInAction(() => {
        if (!this.tapesStore.selectionSourceTape?.ids) {
          this.tapesStore.selectionSourceTape = {
            ids: bookingId,
            type: TapeType.Booking,
          }
        }
      })
    }

    if (propertyId && !selectedArrivalChecklistHomeId) {
      await this.goToPropertyPage(propertyId)
      this.showListingInfo(propertyId)
    }
  }

  async goToPropertyPage(homeId: string) {
    const allHomes = this.apiStore.rawListingsResults?.results
    const propertyIndex = allHomes?.findIndex(home => home.id === homeId)
    if (propertyIndex && propertyIndex > -1) {
      const propertyPage = Math.ceil(propertyIndex / this.navigationStore.pageSize)
      if (this.navigationStore.page !== propertyPage) {
        this.navigationStore.setPage(propertyPage)
      }

      scrollController.scrollToRow(propertyIndex % this.navigationStore.pageSize)
    }
  }

  selectProperty(propertyId?: UUID) {
    if (propertyId) {
      PropertyInfoStore.setCurrentHomeId(propertyId)
    }
    BookingsTapeChartStore.setPropertyIdParam(propertyId)
  }

  showListingInfo(propertyId: UUID) {
    this.selectProperty(propertyId)
    SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartPropertyInfo, {
      screenHasSecondaryMenu: true,
    })
  }

  setSelectedGroupRowId(groupId?: string) {
    this.selectedGroupRowId = groupId
  }
}
